// Resuming a stored Pending Action after login (#1693, spec.md §6.2).
//
// Called once per sign-in transition (see src/pages/Routes.js). Reads any
// locally stored Pending Action, asks the server to verify it
// (employer membership, TTL, nonce replay — functions/deepLinks/
// resolvePendingAction.js), and returns the in-app target to navigate to —
// or null when there is nothing to resume, or the server rejected it
// (expired, replayed, unauthorized, unrecognized), or the request itself
// failed.

import { readPendingAction, clearPendingAction } from './pendingActionStorage'
import { resolvePendingAction } from '../firebase/resolvePendingAction'

let resumeInFlight = null

/**
 * The local record is cleared unconditionally once this settles, whether or
 * not verification succeeded — so the app returns to its target AT MOST
 * once. A network failure therefore also consumes the link; the original
 * Universal/App Link keeps working, so the user can just tap it again.
 *
 * Concurrent callers (e.g. two effects firing in the same tick) share one
 * in-flight promise so the record is read and cleared exactly once.
 * @return {Promise<{path: string, search: string}|null>}
 */
export function resumePendingActionIfAny() {
  if (resumeInFlight) return resumeInFlight

  resumeInFlight = (async () => {
    const stored = await readPendingAction()
    if (!stored) return null

    try {
      const result = await resolvePendingAction({
        path: stored.path,
        search: stored.search,
        createdAt: stored.createdAt,
        nonce: stored.nonce,
      })
      return result?.valid ? {path: result.path, search: result.search || ''} : null
    } catch {
      return null
    } finally {
      clearPendingAction()
      resumeInFlight = null
    }
  })()

  return resumeInFlight
}
